import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Migration {
  id: string
  version: string
  name: string
  description: string
  status: string
  applied_at: string | null
  duration_sec: number | null
}

interface Rollback {
  id: string
  migration_id: string
  reason: string
  rolled_back_at: string
  reapplied_at: string | null
}

export default function MigrationManagerPage() {
  const [migrations, setMigrations] = useState<Migration[]>([])
  const [rollbacks, setRollbacks] = useState<Rollback[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [migRes, rollRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/migrations/'),
      fetch(API_BASE + '/migrations/rollback-history'),
      fetch(API_BASE + '/migrations/statistics')
    ])
    if (migRes.ok) setMigrations((await migRes.json()).migrations || [])
    if (rollRes.ok) setRollbacks((await rollRes.json()).rollbacks || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const applyMigration = async (id: string) => {
    await fetch(API_BASE + '/migrations/' + id + '/apply', { method: 'POST' })
    fetchData()
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'applied': return 'bg-green-100 text-green-800'
      case 'pending': return 'bg-yellow-100 text-yellow-800'
      case 'rolled_back': return 'bg-red-100 text-red-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold mb-8">Database Migration Manager</h1>

        {statistics && (
          <div className="grid grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Migrations</div>
              <div className="text-2xl font-bold">{statistics.total_migrations}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Applied</div>
              <div className="text-2xl font-bold text-green-600">{statistics.applied}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Pending</div>
              <div className="text-2xl font-bold text-yellow-600">{statistics.pending}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Current Version</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.current_version}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Latest Version</div>
              <div className="text-2xl font-bold">{statistics.latest_version}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Rollbacks</div>
              <div className="text-2xl font-bold text-red-600">{statistics.rollbacks}</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-3 gap-6">
          <div className="col-span-2 bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Migrations</h2></div>
            <div className="divide-y">
              {migrations.map(mig => (
                <div key={mig.id} className="p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-lg font-semibold">{mig.version}</span>
                        <span className="font-mono text-sm text-gray-500">{mig.name}</span>
                      </div>
                      <div className="text-sm text-gray-600">{mig.description}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs ${getStatusColor(mig.status)}`}>{mig.status}</span>
                      {mig.status === 'pending' && (
                        <button onClick={() => applyMigration(mig.id)} className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700">Apply</button>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-4 text-sm text-gray-500">
                    {mig.applied_at && <span>Applied: {new Date(mig.applied_at).toLocaleString()}</span>}
                    {mig.duration_sec && <span>Duration: {mig.duration_sec}s</span>}
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Rollback History</h2></div>
            <div className="divide-y">
              {rollbacks.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">No rollbacks recorded</div>
              ) : (
                rollbacks.map(rb => (
                  <div key={rb.id} className="p-4">
                    <div className="font-medium text-sm">{rb.migration_id}</div>
                    <div className="text-sm text-gray-600">{rb.reason}</div>
                    <div className="text-xs text-gray-400 mt-1">
                      Rolled back: {new Date(rb.rolled_back_at).toLocaleString()}
                    </div>
                    {rb.reapplied_at && (
                      <div className="text-xs text-green-600 mt-1">
                        Reapplied: {new Date(rb.reapplied_at).toLocaleString()}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  )
}
